import type { GeocodeError, GeocodeResponse } from "../types";

export interface GeocodeClientOptions {
  basePath: string;
  timeoutMs?: number;
}

export class GeocodeClient {
  private readonly basePath: string;
  private readonly timeoutMs: number;

  constructor(options: GeocodeClientOptions) {
    this.basePath = options.basePath.replace(/\/$/, "");
    this.timeoutMs = options.timeoutMs ?? 7000;
  }

  search(q: string, signal?: AbortSignal): Promise<GeocodeResponse> {
    return this.request("search", { q, language: "fa", limit: 8 }, signal);
  }

  reverse(lat: number, lon: number, signal?: AbortSignal): Promise<GeocodeResponse> {
    return this.request("reverse", { lat, lon, language: "fa" }, signal);
  }

  lookup(osmIds: string, signal?: AbortSignal): Promise<GeocodeResponse> {
    return this.request("lookup", { osm_ids: osmIds, language: "fa" }, signal);
  }

  private async request(
    operation: "search" | "reverse" | "lookup",
    body: Record<string, unknown>,
    outerSignal?: AbortSignal,
  ): Promise<GeocodeResponse> {
    const timeout = new AbortController();
    const timer = window.setTimeout(() => timeout.abort(), this.timeoutMs);
    const signal = combineSignals(timeout.signal, outerSignal);
    const outgoingRequestId = createRequestId();
    try {
      const response = await fetch(`${this.basePath}/${operation}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Request-Id": outgoingRequestId,
        },
        body: JSON.stringify(body),
        signal,
      });
      const responseRequestId = response.headers.get("X-Request-Id") ?? outgoingRequestId;
      if (!response.ok) {
        throw makeError(response.status, responseRequestId);
      }
      const payload = (await response.json()) as GeocodeResponse;
      if (
        !payload ||
        !Array.isArray(payload.results) ||
        !payload.request_id ||
        !payload.dataset_version
      ) {
        throw {
          code: "malformed_response",
          message: "پاسخ جست‌وجو معتبر نیست.",
          status: 502,
        } satisfies GeocodeError;
      }
      return payload;
    } finally {
      window.clearTimeout(timer);
    }
  }
}

function createRequestId(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function makeError(status: number, requestId?: string): GeocodeError {
  const messages: Record<number, [string, string]> = {
    401: ["unauthorized", "دسترسی جست‌وجو موقتاً در دسترس نیست."],
    403: ["forbidden", "دسترسی جست‌وجو مجاز نیست."],
    429: ["rate_limited", "تعداد درخواست‌ها زیاد است. کمی بعد دوباره تلاش کنید."],
  };
  const [code, message] = messages[status] ?? [
    status >= 500 ? "upstream_error" : "request_failed",
    "جست‌وجو با خطا روبه‌رو شد.",
  ];
  return { code, message, status, requestId };
}

function combineSignals(a: AbortSignal, b?: AbortSignal): AbortSignal {
  if (!b) return a;
  const controller = new AbortController();
  const abort = () => controller.abort();
  a.addEventListener("abort", abort, { once: true });
  b.addEventListener("abort", abort, { once: true });
  return controller.signal;
}
